// COAOptions.jsx
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLicense } from "../../context/licenseContext";
import "./COAOptions.css";
import CoaToShowroom from "../CoaToShowroom/CoaToShowroom";
import apiClient from "../../api/apiClient";
import COAUpload from "../COAUpload/COAUpload";
import TransferWeightModal from "../TransferWeightModal/TransferWeightModal";
import OrderTestModal from "../OrderTestModal/OrderTestModal";
import { getPanelStatus, isWholesaleEligible, isThcCompliant, PANEL_LABELS } from "../../utils/coaPanelStatus";
import { QRCodeSVG } from "qrcode.react";
import { toSlug, BADGE_META } from "../../utils/badgeMeta";
import { mixDisplayName } from "../../utils/mixDisplayName";

const MENU_VIEWPORT_MARGIN = 8;

const COAOptions = ({ coa, fetchCoas, title, setCOAProcessing, onCreatePreRoll }) => {
    const { currentLicense } = useLicense(); 
    const optionsRef = useRef();  
    const triggerRef = useRef(null);  
    const menuRef = useRef(null); 
    const [visibleOptions, setVisibleOptions] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const boxRef = useRef(null);
    const [publshTab, setPublishTab] = useState(false);
    const [editNameVisible, setEditNameVisible] = useState(false);
    const [productName, setProductName] = useState(coa.product_name);
    const [industry, setIndustry] = useState("");
    const [transferOpen, setTransferOpen] = useState(false);
    const badgeQrRef = useRef(null); 
    const [orderModalOpen, setOrderModalOpen] = useState(false);

    // Compute which panels are missing
    const panelStatus = getPanelStatus(coa);
    const missingPanels = Object.entries(panelStatus)
        .filter(([, { met }]) => !met)
        .map(([key]) => key);

    const toggleId = coa.product_id || coa.id;

    const handleOptionClick = (action) => async (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log(`${action} COA with ID:`, coa.id);
        if (action === "Edit") {
            setEditNameVisible(true);
        } else if (action === "Publish") {
            setVisibleOptions(false);
            setPublishTab(true);
        } else if (action === "Delete") {
            await apiClient.delete(`/products/${coa.product_id}`);
            fetchCoas();
        }
    };

    const downloadBadge = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const productType = (coa.product_type || '').toLowerCase();
        const isMix = productType === 'mix-flower' || productType === 'mix-preroll' || productType === 'pre-roll';
        const color = isMix ? 'mix' : (coa.color || '').toLowerCase();
        const meta = BADGE_META[color];
        if (!meta) return;

        const qrEl = badgeQrRef.current?.querySelector('svg');
        if (!qrEl) return;

        const variant = meta.strain;
        const response = await fetch(`/Badges/${variant.file}`);
        const svgText = await response.text();

        const parser = new DOMParser();
        const badgeDoc = parser.parseFromString(svgText, 'image/svg+xml');
        const badgeSvg = badgeDoc.querySelector('svg');

        if (coa.product_name) {
            const NS = 'http://www.w3.org/2000/svg';
            const textEl = badgeDoc.createElementNS(NS, 'text');
            textEl.setAttribute('x', '51.03');
            textEl.setAttribute('y', '27.5');
            textEl.setAttribute('text-anchor', 'middle');
            textEl.setAttribute('dominant-baseline', 'central');
            textEl.setAttribute('font-family', 'Arial, Helvetica, sans-serif');
            textEl.setAttribute('font-weight', 'bold');
            textEl.setAttribute('fill', '#231f20');
            const name = mixDisplayName(coa.product_name, coa.product_type, coa.created_at);
            const fontSize = name.length > 20 ? 3 : name.length > 12 ? 3.8 : 4.5;
            textEl.setAttribute('font-size', String(fontSize));
            textEl.textContent = name;
            badgeSvg.appendChild(textEl);
        }

        const qrClone = qrEl.cloneNode(true);
        qrClone.setAttribute('x', String(variant.qx));
        qrClone.setAttribute('y', String(variant.qy));
        qrClone.setAttribute('width', String(variant.qs));
        qrClone.setAttribute('height', String(variant.qs));
        badgeSvg.appendChild(badgeDoc.importNode(qrClone, true));

        const svgString = new XMLSerializer().serializeToString(badgeSvg);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const badgeName = mixDisplayName(coa.product_name, coa.product_type, coa.created_at) || 'badge';
        a.download = `${badgeName.replace(/\s+/g, '-')}-badge.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setVisibleOptions(false);
    };

    const handleClickOutside = useCallback(
        (event) => {
            if (
                optionsRef.current && !optionsRef.current.contains(event.target) &&
                (!menuRef.current || !menuRef.current.contains(event.target))
            ) {
                setVisibleOptions(false);
                setEditNameVisible(false);
            }
        },
        [optionsRef]
    );

    useEffect(() => {
        const handleScroll = () => setVisibleOptions(false);
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [handleClickOutside]);

    // Re-anchor the menu once its real size is known, so it never renders
    // partially off-screen for rows near the bottom/edge of the viewport.
    useLayoutEffect(() => {
        if (visibleOptions !== toggleId || !boxRef.current || !triggerRef.current) return;

        const margin = MENU_VIEWPORT_MARGIN;
        const boxRect = boxRef.current.getBoundingClientRect();
        const triggerRect = triggerRef.current.getBoundingClientRect();

        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        let top = triggerRect.bottom + 4;
        if (boxRect.height > spaceBelow - margin && spaceAbove > spaceBelow) {
            top = triggerRect.top - boxRect.height - 4;
        }
        top = Math.max(margin, Math.min(top, window.innerHeight - boxRect.height - margin));

        let right = window.innerWidth - triggerRect.right;
        right = Math.max(margin, Math.min(right, window.innerWidth - boxRect.width - margin));

        setMenuPos((prev) => (prev.top === top && prev.right === right ? prev : { top, right }));
    }, [visibleOptions, toggleId, editNameVisible]);

    const profile = (currentLicense?.profile || "").toLowerCase();
    const colorLower = (coa.color || "").toLowerCase();
    const validColor = colorLower !== "no color" && colorLower !== "synthetic";

    return (
        <div ref={optionsRef}>
            <div
                ref={triggerRef}
                className="coa-options-container"
                data-tutorial="coa-options-menu"
                onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const isOpening = visibleOptions !== toggleId;
                    if (isOpening && triggerRef.current) {
                        const rect = triggerRef.current.getBoundingClientRect();
                        setMenuPos({
                            top: rect.bottom + 4,
                            right: window.innerWidth - rect.right,
                        });
                    }
                    setVisibleOptions((prevVisible) => (prevVisible === toggleId ? false : toggleId));
                }}
            >
                <div className="options-ball"></div>
                <div className="options-ball"></div>
                <div className="options-ball"></div>
            </div>
            {publshTab && (
                <CoaToShowroom
                    coa={coa}
                    productId={coa.product_id}
                    setPublishTab={setPublishTab}
                    fetchCoas={fetchCoas}
                    industry={industry}
                />
            )}

            {visibleOptions === toggleId && createPortal(
                <div ref={menuRef}>
                    <div
                        className="coa-options-backdrop"
                        onClick={(e) => {
                            e.stopPropagation();
                            setVisibleOptions(false);
                            setEditNameVisible(false);
                        }}
                    />
                    <div
                        ref={boxRef}
                        className="coa-options-box-container fade-in"
                        style={{ position: "fixed", top: menuPos.top, right: menuPos.right, maxHeight: `calc(100vh - ${MENU_VIEWPORT_MARGIN * 2}px)`, overflowY: "auto" }}
                    >
                        {editNameVisible ? (
                            <div className="edit-name-container-options">
                                <input
                                    className="coa-option-edit-input"
                                    type="text"
                                    value={productName}
                                    onChange={(e) => {
                                        setProductName(e.target.value);
                                    }}
                                    autoFocus
                                />
                                <button
                                    className="coa-option-apply-new-name"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        await apiClient.patch(`/products/${coa.product_id}/inventory`, {
                                            product_name: productName,
                                        });
                                        fetchCoas();
                                        setEditNameVisible(false);
                                    }}
                                >
                                    Apply
                                </button>
                            </div>
                        ) : (
                            <div className="coa-option" onClick={handleOptionClick("Edit")}>
                                <span className="coa-option__icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                </span>
                                Edit Name
                            </div>
                        )}

                        {/* Upload COA — only when panels are missing and COA exists */}
                        {setCOAProcessing && coa.id && missingPanels.length > 0 && (
                            <COAUpload
                                setCOAProcessing={setCOAProcessing}
                                fetchCoas={fetchCoas}
                                className="coa-option"
                                coaId={coa.id}
                            />
                        )}

                        {setCOAProcessing && coa.id && missingPanels.length > 0 && (
                            <div
                                className="coa-option"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOrderModalOpen(true);
                                }}
                            >
                                <span className="coa-option__icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                                </span>
                                Order Tests
                            </div>
                        )}

                        {/* Create Wholesale Product — guarded by isWholesaleEligible */}
                        {(profile === "wholesaler" || profile === "cultivator") &&
                            !coa.published &&
                            validColor &&
                            isWholesaleEligible(coa) && (
                                <div className="coa-option" onClick={handleOptionClick("Publish")}>
                                    <span className="coa-option__icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                                    </span>
                                    Create Wholesale Product
                                </div>
                            )}

                        {/* Download Badge — guarded by isThcCompliant only; full-panel/contaminant status is ignored (same rule as COA.js and the public compliance page) */}
                        {(BADGE_META[(coa.color || '').toLowerCase()] || BADGE_META[((coa.product_type || '').toLowerCase().startsWith('mix') ? 'mix' : '')]) &&
                            isThcCompliant(coa) && (
                            <>
                                {/* Hidden QR code — grabbed by downloadBadge via badgeQrRef */}
                                <div ref={badgeQrRef} style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
                                    <QRCodeSVG
                                        value={((coa.product_type || '').toLowerCase().startsWith('mix') || (coa.product_type || '').toLowerCase() === 'pre-roll')
                                            ? `${window.location.origin}/compliance/${toSlug(currentLicense?.company || '')}/mix/${coa.product_id}`
                                            : `${window.location.origin}/compliance/${toSlug(currentLicense?.company || '')}?product=${coa.product_id}`}
                                        size={108}
                                        level="M"
                                        includeMargin={false}
                                        fgColor="#0f172a"
                                        bgColor="#ffffff"
                                    />
                                </div>
                                <div className="coa-option" onClick={downloadBadge}>
                                    <span className="coa-option__icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    </span>
                                    Download Badge
                                </div>
                            </>
                        )}

                        {/* Transfer Inventory — only when a COA and linked product exist */}
                        {coa.id && coa.product_id && (
                            <div
                                className="coa-option"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVisibleOptions(false);
                                    setTransferOpen(true);
                                }}
                            >
                                <span className="coa-option__icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                                </span>
                                Transfer Inventory
                            </div>
                        )}

                        {/* Create Pre-Roll — only for flower products */}
                        {coa.product_id && onCreatePreRoll &&
                            (coa.product_type || "").toLowerCase() === "flower" &&
                            (coa.weight_unit || "").toLowerCase() === "g" &&
                            parseFloat(coa.quantity) > 0 && (
                            <div
                                className="coa-option"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setVisibleOptions(false);
                                    onCreatePreRoll({ ...coa, id: coa.product_id });
                                }}
                            >
                                <span className="coa-option__icon">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                                </span>
                                Create Pre-Roll
                            </div>
                        )}

                        {coa.product_id && (
                            <>
                                <div className="coa-options-divider" />
                                <div className="coa-option coa-option--danger" onClick={handleOptionClick("Delete")}>
                                    <span className="coa-option__icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    </span>
                                    Remove Product
                                </div>
                            </>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {orderModalOpen && (
                <OrderTestModal
                    coa={coa}
                    missingPanels={missingPanels}
                    onClose={() => {
                        setOrderModalOpen(false);
                        setVisibleOptions(false);
                    }}
                    fetchCoas={fetchCoas}
                />
            )}

            {transferOpen && coa.product_id && (
                <TransferWeightModal
                    coaId={coa.id}
                    sourceLicenseNo={currentLicense?.licenseNo}
                    sourceLocationId={coa.product_location_id ?? null}
                    onClose={() => setTransferOpen(false)}
                    onSuccess={fetchCoas}
                />
            )}
        </div>
    );
};

export default COAOptions;
